import React, { useEffect, useState } from "react";

import { getInitImage } from "../utils/asset";
import { useProjectInventory } from "./projectInventory";
import { useAssetsCollection } from "./assetsCollection";

const AssetsContext = React.createContext(null);

export const AssetsProvider = ({ children, assetId: defaultAssetId }) => {
  const [selectedImage, setSelectedImage] = useState("");

  const { clientId, projectId, setClientId, setProjectId } = useProjectInventory();

  const {
    loading,
    loadingMore,
    collection,
    handeRefetch,
    onLoadMore,
    metadata,
    loadingMutation,
    removeAsset,
    createAsset,
    updateAsset,
    removeImage,
    createImage,
    updateImage,
    bulkCreateImages,
    search,
    setSearch,
    limit,
    setLimit,
  } = useAssetsCollection();

  const [currentAssetId, setCurrentAssetId] = useState(defaultAssetId);
  const selectionKey = `${clientId || ""}:${projectId || ""}`;
  const [scopedSelectionKey, setScopedSelectionKey] = useState(selectionKey);

  if (scopedSelectionKey !== selectionKey) {
    setScopedSelectionKey(selectionKey);
    setCurrentAssetId(defaultAssetId);
    setSelectedImage("");
  }

  // If no assetId was provided, lock onto the first loaded asset so subscription prepends
  // don't change which asset is selected. After a client/project switch we reset to
  // `defaultAssetId` (usually undefined) so this locks onto the new scope's first item
  // instead of keeping the previous branch's selection.
  useEffect(() => {
    if (currentAssetId === undefined && collection?.[0]?.id) {
      setCurrentAssetId(collection[0].id);
    }
  }, [currentAssetId, collection]);

  const currentAsset = collection.find(asset => asset.id === currentAssetId) || collection[0];

  useEffect(() => {
    if (currentAsset?.id && !projectId && !clientId) {
      setClientId(currentAsset.client?.id);
      setProjectId(currentAsset.project?.id);
    }
  }, [currentAsset?.id]);

  const setCurrentAsset = selectedAsset => {
    const collectionAsset = collection.find(item => item.id === selectedAsset?.id);

    const asset = collectionAsset || selectedAsset;

    if (asset) {
      setCurrentAssetId(asset.id);
      setSelectedImage(getInitImage(asset));
    } else {
      setCurrentAssetId(null);
      setSelectedImage("");
    }
  };

  const handleCreateAsset = ({ variables, onSuccess, onFailure }) => {
    return createAsset({
      variables: variables,
      onSuccess: ({ asset }) => {
        if (onSuccess) onSuccess({ asset });
        setCurrentAsset(asset);
      },
      onFailure,
    });
  };

  const handleRemoveAsset = ({ variables, onSuccess, onFailure }) => {
    return removeAsset({
      variables,
      onSuccess: ({ asset }) => {
        const nextAsset = collection.find(item => item.id !== asset.id);
        setCurrentAsset(nextAsset);
        onSuccess && onSuccess({ asset });
      },
      onFailure,
    });
  };

  const value = {
    // Inventory Params
    clientId,
    projectId,

    // Collection
    loading,
    loadingMore,
    collection,
    refetchAssets: handeRefetch,
    onLoadMore,
    setLimit,
    limit,
    search,
    setSearch,
    totalCount: metadata.totalCount,

    // Update asset
    currentAsset,
    setCurrentAsset,

    selectedImage,

    onSelectImage: setSelectedImage,

    loadingMutation,
    removeAsset: handleRemoveAsset,
    createAsset: handleCreateAsset,
    updateAsset,
    removeImage,
    createImage,
    updateImage,
    bulkCreateImages,
  };

  return <AssetsContext.Provider value={value}>{children}</AssetsContext.Provider>;
};

export const useAssets = () => React.useContext(AssetsContext);
